import {
  ExportController_paymentModesCsv_v1,
  type OutletResource,
  OutletsController_list_v1,
  type ParkPaymentModeReportResource,
  type PaymentModeFiguresResource,
  type PaymentModeRowResource,
  PaymentModesController_outlet_v1,
  PaymentModesController_park_v1,
} from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden, isNotFound, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { labelForEntryPaymentMode } from './cash-book-entries';
import { saveDownloadedFile } from './cash-book-export';
import { currentBusinessDate, periodProblem, type ReportingPeriod } from './reporting-period';

/**
 * What was taken and handed back per payment mode over a period:
 * `GET /v1/reports/payment-modes`, its per-outlet twin, and the `.csv` beside
 * them (#326).
 *
 * **Rows are rendered as sent, bar one.** The API always answers four rows —
 * Cash, UPI, Card, then `null` — in that order, with zeroes where nothing was
 * taken. A zero Cash, UPI or Card row stays, so the report never looks like it
 * lost Card. `null` is "Unknown": a recorded fact, never folded into cash, and
 * hidden only while every figure in it is zero (#332, see `isShown`). The total
 * row is the envelope's `totals`, not a sum worked out here.
 *
 * **`netPaise` can be negative** — more handed back than came in — and is
 * printed with its sign rather than clamped.
 *
 * **Opens on today alone**, not the month every other Reporting screen opens
 * on: a year-long park read is slow (blossom-rest-api#539), and "what came in
 * by mode today" is the question this is asked most.
 *
 * **The export is the park's.** `payment-modes.csv` takes `from` and `to` and
 * nothing else, so with an outlet chosen it would be a different question from
 * the one on screen. The button is withheld there rather than handing over a
 * file that disagrees with the table.
 */

/** Nothing chosen: the park read, over every outlet the caller reaches. */
const EVERY_OUTLET = '';

/** What the CSV is saved as when the server's own name did not arrive. */
const PAYMENT_MODES_DEFAULT_FILENAME = 'payment-modes.csv';

type ReportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly report: ParkPaymentModeReportResource }
  | { readonly kind: 'failed'; readonly error: unknown };

type ExportState =
  | { readonly kind: 'idle' | 'running' }
  | { readonly kind: 'failed'; error: unknown };

const COLUMNS = ['Payment mode', 'Transactions', 'Takings', 'Refunds', 'Net'] as const;

export function PaymentModes() {
  const client = useApiClient();

  // The park's own day, in Asia/Kolkata — read per render so a screen left open
  // across midnight bounds itself at the new day.
  const today = currentBusinessDate(systemClock);

  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [period, setPeriod] = useState<ReportingPeriod>(() => ({ from: today, to: today }));
  const [state, setState] = useState<ReportState>({ kind: 'idle' });
  const [reloads, setReloads] = useState(0);
  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [exporting, setExporting] = useState<ExportState>({ kind: 'idle' });

  const problem = periodProblem(period, systemClock);
  const complete = period.from !== '' && period.to !== '' && problem === null;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole, retired included: a past period's outlet may since have closed.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // The picker is the only casualty; the park read needs no outlet.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (!complete) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    const query = { from: period.from, to: period.to };

    const answer =
      outletId === EVERY_OUTLET
        ? client.request(PaymentModesController_park_v1, { query, signal: controller.signal })
        : client.request(PaymentModesController_outlet_v1, {
            path: { outletId },
            query,
            signal: controller.signal,
          });

    answer
      .then((report) => {
        if (live) setState({ kind: 'loaded', report });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, period.from, period.to, complete, reloads]);

  /** Through `client.download`, for the bearer-token reason `cash-book-export.ts` gives. */
  const runExport = () => {
    if (!complete || exporting.kind === 'running') return;

    setExporting({ kind: 'running' });

    client
      .download(ExportController_paymentModesCsv_v1, {
        query: { from: period.from, to: period.to },
      })
      .then((file) => {
        saveDownloadedFile(file, PAYMENT_MODES_DEFAULT_FILENAME);
        setExporting({ kind: 'idle' });
      })
      .catch((error: unknown) => {
        setExporting({ kind: 'failed', error });
      });
  };

  const outletItems = useMemo(
    () => [
      { label: 'Every outlet in reach', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const openDays =
    state.kind === 'loaded' ? state.report.period.days.filter((day) => day.state === 'OPEN') : [];

  return (
    <Screen
      title="Payment modes"
      lede="what came in, by how it was paid"
      description="Takings, refunds and net for cash, UPI and card over a period, gate tickets included. Unknown is money recorded with no mode — a back-office entry — and is never counted as cash. For a closed day these match Day close mode for mode."
      action={
        <Button
          onClick={runExport}
          disabled={!complete || outletId !== EVERY_OUTLET || exporting.kind === 'running'}
        >
          {exporting.kind === 'running' ? (
            <IconLoader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <IconDownload data-icon="inline-start" />
          )}
          {exporting.kind === 'running' ? 'Exporting…' : 'Export CSV'}
        </Button>
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="payment-modes-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                setOutletId(next ?? EVERY_OUTLET);
              }}
            >
              <SelectTrigger
                id="payment-modes-outlet"
                aria-label="Outlet"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outletItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-modes-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="payment-modes-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, from: next }));
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-modes-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="payment-modes-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, to: next }));
              }}
            />
          </div>

          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          {openDays.length > 0 && (
            <ScreenFact label="Not closed yet">
              {openDays.map((day) => formatBusinessDate(day.businessDate)).join(', ')}
              <br />
              <span className="text-muted-foreground">
                {openDays.length === 1 ? 'This day is' : 'These days are'} still trading, so the
                figures can still move.
              </span>
            </ScreenFact>
          )}

          {outletId !== EVERY_OUTLET && (
            <p className="text-sm text-muted-foreground">
              The export covers every outlet. Choose Every outlet in reach to export.
            </p>
          )}

          {exporting.kind === 'failed' && (
            <p role="status" className="text-sm text-destructive">
              The export could not be made. {messageForFailure(exporting.error)}
            </p>
          )}
        </>
      }
    >
      <ScreenTable label="Payment modes">
        <TableHeader>
          <TableRow>
            {COLUMNS.map((column, index) => (
              <TableHead key={column} className={index === 0 ? '' : 'text-right'}>
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS.length} rows={4} />}

        {state.kind === 'idle' && (
          <TableNotice columns={COLUMNS.length}>
            <EmptyState
              title="Choose a period to report on"
              description="Payment modes are read over a range of trading days. Pick the two that bound it."
            />
          </TableNotice>
        )}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS.length}>
            {isForbidden(state.error) ? (
              <EmptyState
                title="This account cannot read payment modes"
                description="This report is for owners and managers. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : isNotFound(state.error) ? (
              <EmptyState
                title="That outlet is not one this account can report on"
                description="It may have been removed, or it may be outside what this account reaches. Choose another outlet."
              />
            ) : (
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
                title="This report could not be read"
              />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' && (
          <>
            <TableBody>
              {state.report.rows.filter(isShown).map((row) => (
                <FiguresRow
                  key={row.paymentMode ?? 'UNKNOWN'}
                  label={
                    row.paymentMode === null ? 'Unknown' : labelForEntryPaymentMode(row.paymentMode)
                  }
                  figures={row}
                />
              ))}
            </TableBody>
            <TableFooter>
              <FiguresRow label="Total" figures={state.report.totals} />
            </TableFooter>
          </>
        )}
      </ScreenTable>
    </Screen>
  );
}

/**
 * Unknown is withheld only while it is all zeroes (#332): no writer records a
 * null mode today. The moment any figure is not zero it shows, or the rows stop
 * summing to the Total. Cash, UPI and Card always show — "no cash today" is
 * what someone closing that drawer wants to read.
 */
function isShown(row: PaymentModeRowResource): boolean {
  return (
    row.paymentMode !== null ||
    row.transactionCount !== 0 ||
    row.takingsPaise !== 0 ||
    row.refundsPaise !== 0 ||
    row.netPaise !== 0
  );
}

function FiguresRow({ label, figures }: { label: string; figures: PaymentModeFiguresResource }) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell className="text-right font-mono numeric">{figures.transactionCount}</TableCell>
      <TableCell className="text-right font-mono numeric">
        {formatPaise(figures.takingsPaise)}
      </TableCell>
      <TableCell className="text-right font-mono numeric">
        {formatPaise(figures.refundsPaise)}
      </TableCell>
      <TableCell className="text-right font-mono numeric">
        {formatPaise(figures.netPaise)}
      </TableCell>
    </TableRow>
  );
}
